import {
  type UseMutationResult,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import api from '@/axios';
import {
  GET_PRODUCTION_DECLARATION_KEY,
  GET_PURCHASE_ORDER_ACTIVITY_KEY,
  UPDATE_PRODUCTION_DECLARATION_KEY,
} from '@/keys';
import type { ProductionDeclaration } from '@/types/core/purchase-order/production-declaration';

export type UpdateProductionDeclarationParams = {
  id: string;
  token?: string;
  isApproved?: boolean;
  recipients?: string[];
  message?: string;
};

const updateProductionDeclaration = async ({
  id,
  token,
  isApproved,
  recipients,
  message,
}: UpdateProductionDeclarationParams): Promise<void> => {
  await api.patch(`/production-declarations/${id}`, {
    ...(token !== undefined && { token }),
    ...(isApproved !== undefined && { isApproved }),
    ...(recipients !== undefined && { recipients }),
    ...(message !== undefined && { message }),
  });
};

export const useUpdateProductionDeclaration = (): UseMutationResult<
  void,
  Error,
  UpdateProductionDeclarationParams
> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: UPDATE_PRODUCTION_DECLARATION_KEY,
    mutationFn: updateProductionDeclaration,
    onSuccess: async (_data, { id, token }) => {
      const queryKey = [...GET_PRODUCTION_DECLARATION_KEY, id, token ?? ''];
      const productionDeclaration =
        queryClient.getQueryData<ProductionDeclaration>(queryKey);

      await queryClient.invalidateQueries({
        queryKey,
      });

      if (productionDeclaration?.purchaseOrderId) {
        await queryClient.invalidateQueries({
          queryKey: [
            ...GET_PURCHASE_ORDER_ACTIVITY_KEY,
            String(productionDeclaration.purchaseOrderId),
          ],
        });
      }
    },
  });
};
